import React from "react";
import { FileUploadComponent } from "../../../../components/fileUpload/FileUploadComponent";
import { Stack, Typography } from "@mui/material";
import {
  NavigateToFunctions,
  processProjectParams,
} from "../../../../routes/RoutesHelper";
import { Instance } from "mobx-state-tree";
import {
  RespondentSPoCMappingStore,
  ValidateRespondentSPoCMappingErrors,
  GetPresignedDocumentUploadURLForRespondentSpocMappingErrors,
} from "../../store/RespondentSPoCMappingStore";
import {
  EMPTY_CHARACTER,
  ICON_SIZE,
  MAX_RESPONDENT_SPOC_MAPPING_FILE_SIZE_IN_BYTES,
  MIN_FILE_SIZE_IN_BYTES,
} from "@pulse/shared-components";
import { getUploadErrorDescriptions } from "../../../utils/FileUploadUtils";
import { TFunction } from "i18next";
import {
  AlertCircle,
  FoundationColorTokens,
  Spacing,
  Typography as LeoTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { FileUploadedDescription } from "../../../../components/fileUpload/FileUploadedDescription";
import { UploadValidationLoader } from "../../../../components/fileUpload/UploadValidationLoader";
import { getIconProps } from "@pulse/shared-components";

export interface RespondentSPoCUploadPaneProps {
  respondentSPoCMappingStore: Instance<
    typeof RespondentSPoCMappingStore
  > | null;
  navigateTo: NavigateToFunctions;
  t: TFunction;
  spacing: Spacing;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
}

export const RespondentSPoCUploadPane = ({
  respondentSPoCMappingStore,
  navigateTo,
  t,
  spacing,
  typography,
  tokens,
}: RespondentSPoCUploadPaneProps): React.ReactElement => {
  const iconProps = getIconProps(tokens.iconError, ICON_SIZE.default);

  const getUploadErrors = (): React.ReactElement => {
    let errorDescription: string = EMPTY_CHARACTER;
    if (respondentSPoCMappingStore?.validationErrors) {
      errorDescription = getUploadErrorDescriptions(
        respondentSPoCMappingStore?.validationErrors,
        t,
        MAX_RESPONDENT_SPOC_MAPPING_FILE_SIZE_IN_BYTES,
        MIN_FILE_SIZE_IN_BYTES,
      );
    } else if (respondentSPoCMappingStore?.rpcErrors) {
      switch (respondentSPoCMappingStore?.rpcErrors) {
        case GetPresignedDocumentUploadURLForRespondentSpocMappingErrors.SpocAssignmentInProgress: {
          errorDescription = t(
            "projects.respondentList.respondentUpdateProgressDescription",
          );
          break;
        }
        case ValidateRespondentSPoCMappingErrors.SpocAssignmentInProgress: {
          errorDescription = t(
            "projects.respondentList.respondentUpdateProgressDescription",
          );
          break;
        }
        case ValidateRespondentSPoCMappingErrors.ProjectArchived: {
          errorDescription = t(
            "projects.respondentList.projectArchivedDescription",
          );
          break;
        }
        case ValidateRespondentSPoCMappingErrors.UnknownFile: {
          errorDescription = t(
            "common.uploadDialog.unexpectedErrorDescription",
          );
          break;
        }
        default: {
          errorDescription = t(
            "common.uploadDialog.unexpectedErrorDescription",
          );
        }
      }
    }

    return (
      <Stack gap={spacing.spaceXXS}>
        <Typography {...typography.s2} color={tokens.label}>
          {t("projects.respondentList.fileDetails")}
        </Typography>
        <Typography {...typography.b2} color={tokens.label}>
          {respondentSPoCMappingStore?.fileName}
        </Typography>
        <Stack direction="row" gap={spacing.spaceXXS}>
          <AlertCircle {...iconProps} />
          <Typography {...typography.b2} color={tokens.labelError}>
            {errorDescription}
          </Typography>
        </Stack>
      </Stack>
    );
  };

  const fileUploadHandler = (file: File): Promise<void> => {
    respondentSPoCMappingStore?.setFileName(file.name);
    return processProjectParams(async (projectId) => {
      await respondentSPoCMappingStore?.uploadCSVFile(file, projectId);
    }, navigateTo);
  };

  if (respondentSPoCMappingStore?.isRPCLoading) {
    return (
      <UploadValidationLoader
        fileName={respondentSPoCMappingStore?.fileName}
        t={t}
        typography={typography}
        tokens={tokens}
        spacing={spacing}
      />
    );
  } else if (
    respondentSPoCMappingStore?.validationErrors ??
    respondentSPoCMappingStore?.rpcErrors
  ) {
    if (respondentSPoCMappingStore?.isFileInvalid) {
      return (
        <Stack gap={spacing.spaceMD}>
          <FileUploadComponent fileUploadHandler={fileUploadHandler} />
          <Stack gap={spacing.spaceXXS}>
            <Typography {...typography.s2} color={tokens.label}>
              {t("common.uploadDialog.errorsFoundText")}
            </Typography>
            <Typography
              {...typography.b2}
              color={tokens.labelError}
              whiteSpace="pre-line"
            >
              {respondentSPoCMappingStore.invalidFileDetails}
            </Typography>
          </Stack>
        </Stack>
      );
    }
    return (
      <Stack gap={spacing.spaceMD}>
        <FileUploadComponent fileUploadHandler={fileUploadHandler} />
        {getUploadErrors()}
      </Stack>
    );
  } else {
    if (respondentSPoCMappingStore?.hasFileUploaded) {
      return (
        <FileUploadedDescription
          fileName={respondentSPoCMappingStore?.fileName}
          t={t}
          typography={typography}
          tokens={tokens}
          spacing={spacing}
        />
      );
    } else {
      return <FileUploadComponent fileUploadHandler={fileUploadHandler} />;
    }
  }
};
